import { FORBIDDEN, UNAUTHORIZED } from '../constants/apiStatusCodes'

/** Mensajes UI cuando el API no devuelve texto usable. */
export const API_ERROR_MESSAGES = {
  FORBIDDEN: 'No tienes permiso para realizar esta acción.',
  DEFAULT: 'No se pudo completar la acción. Intenta nuevamente.'
}

export const API_ERROR_TOAST_TITLES = {
  ERROR: 'No se pudo completar la acción',
  FORBIDDEN: 'Sin permiso'
}

/** Códigos / claves del backend → copy amigable (rbac, etc.). */
const API_ERROR_CODE_ALIASES = {
  FORBIDDEN: API_ERROR_MESSAGES.FORBIDDEN,
  FORBIDDEN_FIELD: API_ERROR_MESSAGES.FORBIDDEN
}

const flattenMessages = (messages) => {
  if (!messages) return []
  if (typeof messages === 'string') return [messages]
  if (Array.isArray(messages)) return messages.flatMap(flattenMessages)
  if (typeof messages === 'object') return Object.values(messages).flatMap(flattenMessages)
  return []
}

/**
 * Extrae texto de un JSON de error del API (mismo criterio que precios especiales y geocerca).
 */
export function extractErrorMessage(payload, fallback = 'No se pudo procesar la solicitud.') {
  const messages = [
    ...(payload?.message ? flattenMessages(payload.message) : []),
    ...(payload?.error ? flattenMessages(payload.error) : []),
    ...(payload?.errors ? flattenMessages(payload.errors) : []),
    ...(payload?.reason ? flattenMessages(payload.reason) : [])
  ].filter(Boolean)

  return messages.length > 0 ? messages.join(' ') : fallback
}

/**
 * Traduce códigos machine-readable; deja pasar mensajes ya legibles (p. ej. «Permisos insuficientes»).
 */
export function normalizeApiErrorCode(raw) {
  if (raw == null || raw === '') return null
  const text = String(raw).trim()
  return API_ERROR_CODE_ALIASES[text] ?? text
}

export function resolveApiErrorMessage({ status, payload, fallback = API_ERROR_MESSAGES.DEFAULT }) {
  const rawFromBody = extractErrorMessage(payload, '')
  const fromBody = normalizeApiErrorCode(rawFromBody)
  if (fromBody) return fromBody

  if (status === FORBIDDEN) return API_ERROR_MESSAGES.FORBIDDEN
  if (status === UNAUTHORIZED) return null

  return fallback
}

/** @param {Response} response */
export async function parseApiErrorPayload(response) {
  try {
    return await response.clone().json()
  } catch {
    return null
  }
}

export async function getApiErrorMessageFromResponse(responseOrStatus, payload) {
  if (responseOrStatus && typeof responseOrStatus === 'object' && 'status' in responseOrStatus) {
    const response = responseOrStatus
    const body = payload ?? (await parseApiErrorPayload(response))
    return resolveApiErrorMessage({ status: response.status, payload: body })
  }

  return resolveApiErrorMessage({ status: responseOrStatus, payload })
}

export async function getApiErrorToastContent(responseOrStatus, payload) {
  const status =
    responseOrStatus && typeof responseOrStatus === 'object' && 'status' in responseOrStatus
      ? responseOrStatus.status
      : responseOrStatus

  const message = await getApiErrorMessageFromResponse(responseOrStatus, payload)
  const title =
    status === FORBIDDEN ? API_ERROR_TOAST_TITLES.FORBIDDEN : API_ERROR_TOAST_TITLES.ERROR

  return { title, message }
}
